import { useCallback, useEffect, useRef, useState } from "react";

import { Capacitor } from "@capacitor/core";

import { Peer, type Message, type PeerState } from "@nav-games/multiplayer";
import { BleTransport } from "@nav-games/multiplayer-ble";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { CardinalBuoy, CellFill, RockGlyph, WaveGlyph } from "../../ui/SoundingsGlyphs";
import {
  NORMAL_CONFIG,
  coordLabel,
  emptyBoard,
  finalizeBoard,
  isMultiMessage,
  parseCoord,
  placeFleetShip,
  randomFleet,
  removeShipAt,
  revealCell,
  type Board,
  type Cell,
  type CellState,
  type Direction,
  type FireMessage,
  type FireResultMessage,
  type RevealResult,
  type ShipSpec,
} from "@nav-games/soundings";

interface SoundingsMultiProps {
  onNavigate: (to: string) => void;
}

type Role = "host" | "guest";

interface PlayingState {
  kind: "playing";
  peer: Peer;
  /// My private chart — ships visible to me, the renderer marks
  /// opponent's incoming shots on top of it.
  myBoard: Board;
  /// Tracking grid — what I've learned about the opponent's chart
  /// from my own shots. Each cell carries only the revealed state
  /// (no underlying content), plus the cardinal direction or sunk
  /// ship name when applicable.
  tracking: TrackingCell[][];
  /// My grounding-lives. Each time my shot hits a hazard on the
  /// opponent's board I lose one.
  myLives: number;
  /// Number of opponent ships sunk by me.
  opponentSunk: number;
  /// Number of my ships sunk by the opponent.
  mySunk: number;
  myShots: number;
  whoseTurn: "me" | "them";
  banner: string;
}

interface TrackingCell {
  state: CellState;
  /// Only set when state === "cardinal".
  cardinalDirection?: Direction;
  /// Only set when state === "sunk" — the last sunk ship name
  /// announced for this cell.
  sunkShipName?: string;
}

interface PlacingState {
  kind: "placing";
  role: Role;
  peer: Peer;
  /// The board being arranged — ships only, no hazards yet.
  board: Board;
  /// We've finished placing and sent our ready; waiting on the opponent.
  localReady: boolean;
  /// The opponent has signalled ready.
  remoteReady: boolean;
  /// Empty tracking grid carried into the playing phase.
  tracking: TrackingCell[][];
}

type Phase =
  | { kind: "pick-role" }
  | { kind: "connecting"; role: Role }
  | PlacingState
  | PlayingState
  | { kind: "ended"; reason: string; won?: boolean };

const TOTAL_SHIPS = NORMAL_CONFIG.fleet.length;

/// Soundings — Bluetooth 1:1 multiplayer. After connecting, each player
/// arranges their own 10 × 10 fleet in a placement phase and taps Ready;
/// once both are ready, rocks + cardinals are scattered and the host
/// fires first, then alternates. Each fire is exchanged as a single
/// FIRE / FIRE-RESULT pair over the multiplayer Peer transport.
/// Native-only — browsers don't get BLE.
export function SoundingsMulti({ onNavigate }: SoundingsMultiProps) {
  const native = Capacitor.isNativePlatform();
  const [phase, setPhase] = useState<Phase>({ kind: "pick-role" });
  const [helpOpen, setHelpOpen] = useState(false);
  // Placement-phase UI: which ship is selected for placing, and its
  // orientation. Live at component scope so they survive re-renders.
  const [selectedShipId, setSelectedShipId] = useState<string | null>(null);
  const [horizontal, setHorizontal] = useState(true);
  // A "ready" message can arrive before we've pressed ready ourselves;
  // stash it so the handshake completes whichever order it lands in.
  const remoteReadyRef = useRef(false);
  // Stash one peer reference so the unmount cleanup doesn't depend
  // on the discriminated-union state holding it.
  const peerRef = useRef<Peer | null>(null);

  // Keep a sensible ship selected during placement: if the current
  // pick is already placed (or none), advance to the first unplaced.
  useEffect(() => {
    if (phase.kind !== "placing") return;
    const placed = new Set(phase.board.ships.map((s) => s.spec.id));
    if (!selectedShipId || placed.has(selectedShipId)) {
      const next = NORMAL_CONFIG.fleet.find((s) => !placed.has(s.id));
      setSelectedShipId(next ? next.id : null);
    }
  }, [phase, selectedShipId]);

  useEffect(() => {
    return () => {
      const p = peerRef.current;
      if (p) void p.close("page closed");
      peerRef.current = null;
    };
  }, []);

  const startSession = useCallback(
    async (role: Role) => {
      if (!native) return;
      setPhase({ kind: "connecting", role });
      const peer = new Peer({
        transport: new BleTransport(),
        role,
        gameType: "soundings",
      });
      peerRef.current = peer;

      const tracking = makeEmptyTracking(NORMAL_CONFIG.cols, NORMAL_CONFIG.rows);
      remoteReadyRef.current = false;

      peer.onStateChange((s: PeerState) => {
        if (s === "ready") {
          // Connection up — go to the placement phase. Start from a
          // random arrangement the player can keep or rework.
          setPhase({
            kind: "placing",
            role,
            peer,
            board: randomFleet(NORMAL_CONFIG),
            localReady: false,
            remoteReady: remoteReadyRef.current,
            tracking,
          });
        } else if (s === "closed") {
          setPhase({ kind: "ended", reason: "Connection closed." });
        }
      });

      peer.onMessage((m: Message) => {
        if (m.type !== "game") return;
        const payload = m.payload;
        if (!isMultiMessage(payload)) return;
        if (payload.type === "ready") {
          handleRemoteReady();
        } else if (payload.type === "fire") {
          handleIncomingFire(payload);
        } else if (payload.type === "fire-result") {
          handleIncomingResult(payload);
        }
      });

      try {
        await peer.start();
      } catch (e) {
        setPhase({ kind: "ended", reason: (e as Error).message });
      }
    },
    [native],
  );

  /// Opponent fired at us — look up the cell on our board, apply
  /// revealCell to record the shot, and send back the result.
  function handleIncomingFire(fire: FireMessage) {
    setPhase((cur) => {
      if (cur.kind !== "playing") return cur;
      const { board: nextBoard, result } = revealCell(
        cloneBoard(cur.myBoard),
        fire.col,
        fire.row,
      );
      const fireResult: FireResultMessage = {
        type: "fire-result",
        col: fire.col,
        row: fire.row,
        event: networkEvent(result.event),
        defenderLivesRemaining: nextBoard.lives,
        fleetSunk: nextBoard.status === "won",
      };
      // Add cardinal direction / sunk name where applicable.
      if (fireResult.event === "cardinal") {
        const cell = nextBoard.cells[fire.row]?.[fire.col];
        if (cell?.content.kind === "cardinal") {
          fireResult.cardinalDirection = cell.content.direction;
        }
      }
      if (result.event === "sunk" && result.sunkShipName) {
        fireResult.sunkShipName = result.sunkShipName;
      }
      void cur.peer.sendGame(fireResult);

      // Did the opponent just sink my whole fleet?
      const mySunkCount = nextBoard.ships.filter((s) => s.sunk).length;
      if (nextBoard.status === "won") {
        return {
          kind: "ended",
          reason: "Fleet sunk.",
          won: false,
        };
      }
      return {
        ...cur,
        myBoard: nextBoard,
        mySunk: mySunkCount,
        whoseTurn: "me",
        banner: describeIncoming(result),
      };
    });
  }

  /// Result of our outgoing fire came back. Update the tracking grid
  /// and flip the turn.
  function handleIncomingResult(r: FireResultMessage) {
    setPhase((cur) => {
      if (cur.kind !== "playing") return cur;
      const nextTracking = cur.tracking.map((row) => [...row]);
      const cell: TrackingCell = { state: stateFromEvent(r.event) };
      if (r.cardinalDirection) cell.cardinalDirection = r.cardinalDirection;
      if (r.sunkShipName) cell.sunkShipName = r.sunkShipName;
      nextTracking[r.row]![r.col] = cell;

      // If sunk: also re-tag any prior hits for the same ship — we
      // don't know which cells they were, but the renderer only cares
      // about this cell.

      const nextLives = r.event === "grounded" ? cur.myLives - 1 : cur.myLives;
      const opponentSunk = r.event === "sunk" ? cur.opponentSunk + 1 : cur.opponentSunk;
      const fleetSunk = r.fleetSunk;
      const livesOut = nextLives <= 0;

      if (fleetSunk || livesOut) {
        return {
          kind: "ended",
          reason: fleetSunk ? "Fleet sunk." : "Out of lives.",
          won: fleetSunk,
        };
      }
      return {
        ...cur,
        tracking: nextTracking,
        myLives: nextLives,
        opponentSunk,
        whoseTurn: "them",
        banner: describeOutgoing(r),
      };
    });
  }

  // ── Placement phase ──────────────────────────────────────────────

  function startPlaying(cur: PlacingState): PlayingState {
    // Lock in the arrangement and scatter the rocks + cardinals.
    const myBoard = finalizeBoard(cur.board);
    return {
      kind: "playing",
      peer: cur.peer,
      myBoard,
      tracking: cur.tracking,
      myLives: myBoard.lives,
      opponentSunk: 0,
      mySunk: 0,
      myShots: 0,
      whoseTurn: cur.role === "host" ? "me" : "them",
      banner:
        cur.role === "host"
          ? "Your move — fire at coordinates you suspect contain ships."
          : "Host fires first.",
    };
  }

  function handleRemoteReady() {
    remoteReadyRef.current = true;
    setPhase((cur) => {
      if (cur.kind !== "placing") return cur;
      return cur.localReady ? startPlaying(cur) : { ...cur, remoteReady: true };
    });
  }

  function onReady() {
    setPhase((cur) => {
      if (cur.kind !== "placing" || cur.localReady) return cur;
      if (cur.board.ships.length < NORMAL_CONFIG.fleet.length) return cur;
      void cur.peer.sendGame({ type: "ready" });
      return cur.remoteReady || remoteReadyRef.current
        ? startPlaying(cur)
        : { ...cur, localReady: true };
    });
  }

  function onPlaceCellTap(col: number, row: number) {
    setPhase((cur) => {
      if (cur.kind !== "placing" || cur.localReady) return cur;
      const cell = cur.board.cells[row]?.[col];
      if (!cell) return cur;
      // Tap a placed ship to pick it back up.
      if (cell.content.kind === "ship") {
        return { ...cur, board: removeShipAt(cur.board, col, row) };
      }
      // Otherwise drop the selected ship here (if it fits).
      if (!selectedShipId) return cur;
      const spec = NORMAL_CONFIG.fleet.find((s) => s.id === selectedShipId);
      if (!spec) return cur;
      const placed = placeFleetShip(cur.board, spec, col, row, horizontal);
      return placed ? { ...cur, board: placed } : cur;
    });
  }

  function onRandomise() {
    setPhase((cur) =>
      cur.kind === "placing" && !cur.localReady
        ? { ...cur, board: randomFleet(NORMAL_CONFIG) }
        : cur,
    );
  }

  function onClearPlacement() {
    setPhase((cur) =>
      cur.kind === "placing" && !cur.localReady
        ? { ...cur, board: emptyBoard(NORMAL_CONFIG) }
        : cur,
    );
  }

  function onFire(col: number, row: number) {
    setPhase((cur) => {
      if (cur.kind !== "playing") return cur;
      if (cur.whoseTurn !== "me") return cur;
      if (cur.tracking[row]?.[col]?.state !== "hidden") return cur;
      const fire: FireMessage = { type: "fire", col, row };
      void cur.peer.sendGame(fire);
      return {
        ...cur,
        myShots: cur.myShots + 1,
        banner: `You fired at ${coordLabel(col, row)} — awaiting result.`,
      };
    });
  }

  // ── Rendering ────────────────────────────────────────────────────

  if (!native) {
    return (
      <div className="card">
        <h2>Soundings — Multi-player</h2>
        <p className="muted">
          BLE peer-to-peer needs the iOS or Android app — browsers
          don't expose Bluetooth peripheral mode. Install the mobile
          app and open Soundings from there.
        </p>
        <button
          className="secondary"
          onClick={() => onNavigate("/play/soundings")}
        >
          ← Back to single-player
        </button>
      </div>
    );
  }

  if (phase.kind === "pick-role") {
    return <RolePicker onPick={startSession} onBack={() => onNavigate("/play/multiplayer")} />;
  }
  if (phase.kind === "connecting") {
    return <Connecting role={phase.role} onCancel={() => onNavigate("/play/multiplayer")} />;
  }
  if (phase.kind === "placing") {
    return (
      <PlacementView
        state={phase}
        selectedShipId={selectedShipId}
        horizontal={horizontal}
        onSelectShip={setSelectedShipId}
        onToggleOrientation={() => setHorizontal((h) => !h)}
        onCellTap={onPlaceCellTap}
        onRandomise={onRandomise}
        onClear={onClearPlacement}
        onReady={onReady}
        onLeave={() => onNavigate("/play/multiplayer")}
      />
    );
  }
  if (phase.kind === "ended") {
    return (
      <EndCard
        reason={phase.reason}
        {...(phase.won === undefined ? {} : { won: phase.won })}
        onAgain={() => setPhase({ kind: "pick-role" })}
        onBack={() => onNavigate("/play/multiplayer")}
      />
    );
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "buoyage" : null} onClose={() => setHelpOpen(false)} />
      <PlayingView
        state={phase}
        onFire={onFire}
        onLeave={() => onNavigate("/play/multiplayer")}
      />
    </>
  );
}

function RolePicker({
  onPick,
  onBack,
}: {
  onPick: (r: Role) => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2 style={{ marginTop: 0 }}>Soundings — 1:1 Bluetooth</h2>
      <p className="muted">
        Two phones within ~10 m. After connecting, each player{" "}
        <strong>arranges their own fleet</strong> on a 10 × 10 chart, then
        you take turns firing. Grounding on an opponent's rock costs you a
        life; sinking all their ships wins.
      </p>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Host</h3>
        <p className="small muted">
          Advertise the game. The other phone scans for you and
          connects. Host fires first.
        </p>
        <button onClick={() => onPick("host")}>Host a game</button>
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Join</h3>
        <p className="small muted">
          Scan for a hosted game on a nearby phone and connect.
        </p>
        <button onClick={() => onPick("guest")}>Join a game</button>
      </div>
      <button className="secondary" onClick={onBack}>
        ← Back
      </button>
    </>
  );
}

function Connecting({ role, onCancel }: { role: Role; onCancel: () => void }) {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>
        {role === "host" ? "Hosting…" : "Searching for a host…"}
      </h3>
      <p className="small muted">
        {role === "host"
          ? "Waiting for the other phone to connect. Tell them to tap 'Join a game'."
          : "Scanning for a hosted Soundings game in range."}
      </p>
      <button className="secondary" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

const PLACE_LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  color: "var(--text-muted)",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

function PlacementRow({
  rowIdx,
  cols,
  board,
  locked,
  onCellTap,
}: {
  rowIdx: number;
  cols: number;
  board: Board;
  locked: boolean;
  onCellTap: (col: number, row: number) => void;
}) {
  return (
    <>
      <div style={PLACE_LABEL_STYLE}>{rowIdx + 1}</div>
      {Array.from({ length: cols }, (_, c) => {
        const isShip = board.cells[rowIdx]![c]!.content.kind === "ship";
        return (
          <button
            key={`pc-${c}-${rowIdx}`}
            disabled={locked}
            onClick={() => onCellTap(c, rowIdx)}
            title={coordLabel(c, rowIdx)}
            aria-label={`${coordLabel(c, rowIdx)}${isShip ? " — ship" : ""}`}
            style={{
              minHeight: 0,
              padding: 0,
              border: "1px solid #cdd4dd",
              background: isShip ? "#1c2530" : "#eaf3fb",
              cursor: locked ? "default" : "pointer",
            }}
          />
        );
      })}
    </>
  );
}

function PlacementView({
  state,
  selectedShipId,
  horizontal,
  onSelectShip,
  onToggleOrientation,
  onCellTap,
  onRandomise,
  onClear,
  onReady,
  onLeave,
}: {
  state: PlacingState;
  selectedShipId: string | null;
  horizontal: boolean;
  onSelectShip: (id: string) => void;
  onToggleOrientation: () => void;
  onCellTap: (col: number, row: number) => void;
  onRandomise: () => void;
  onClear: () => void;
  onReady: () => void;
  onLeave: () => void;
}) {
  const board = state.board;
  const { cols, rows } = board.config;
  const placed = new Set(board.ships.map((s) => s.spec.id));
  const fleet: readonly ShipSpec[] = NORMAL_CONFIG.fleet;
  const allPlaced = placed.size === fleet.length;
  const locked = state.localReady;
  const cellSize = "28px";

  return (
    <>
      <h2 style={{ marginTop: 0 }}>Set up your chart</h2>
      {locked ? (
        <p className="muted">
          Ready — waiting for your opponent to finish placing…
        </p>
      ) : (
        <p className="muted">
          Place your whole fleet, then tap <strong>Ready</strong>. Tap a ship
          to select it, tap the grid to drop it, and tap a placed ship to pick
          it up again. Rocks and cardinals are added automatically once both
          players are ready.
        </p>
      )}

      {!locked && (
        <div className="card">
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {fleet.map((spec) => {
              const isPlaced = placed.has(spec.id);
              const isSel = selectedShipId === spec.id;
              return (
                <button
                  key={spec.id}
                  className={isSel ? "" : "secondary"}
                  onClick={() => onSelectShip(spec.id)}
                  style={{ opacity: isPlaced ? 0.5 : 1, fontSize: 13, padding: "6px 10px" }}
                >
                  {isPlaced ? "✓ " : ""}
                  {spec.name} ({spec.length})
                </button>
              );
            })}
          </div>
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button className="secondary" onClick={onToggleOrientation}>
              {horizontal ? "→ Horizontal" : "↓ Vertical"}
            </button>
            <button className="secondary" onClick={onRandomise}>
              Randomise
            </button>
            <button className="secondary" onClick={onClear}>
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 8, overflow: "auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `auto repeat(${cols}, ${cellSize})`,
            gridTemplateRows: `auto repeat(${rows}, ${cellSize})`,
            gap: 2,
            width: "fit-content",
            margin: "0 auto",
          }}
        >
          <div />
          {Array.from({ length: cols }, (_, c) => (
            <div key={`ph-${c}`} style={PLACE_LABEL_STYLE}>
              {String.fromCharCode(65 + c)}
            </div>
          ))}
          {Array.from({ length: rows }, (_, r) => (
            <PlacementRow
              key={`pr-${r}`}
              rowIdx={r}
              cols={cols}
              board={board}
              locked={locked}
              onCellTap={onCellTap}
            />
          ))}
        </div>
      </div>

      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        {!locked && (
          <button onClick={onReady} disabled={!allPlaced}>
            {allPlaced
              ? "Ready — lock in fleet"
              : `Place all ${fleet.length} ships first`}
          </button>
        )}
        <button className="secondary" onClick={onLeave}>
          ← Leave
        </button>
      </div>
    </>
  );
}

function PlayingView({
  state,
  onFire,
  onLeave,
}: {
  state: PlayingState;
  onFire: (col: number, row: number) => void;
  onLeave: () => void;
}) {
  return (
    <>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>Soundings — 1:1</h2>
        <span
          className="pill"
          style={{
            background:
              state.whoseTurn === "me"
                ? "var(--accent)"
                : "var(--bg-elev)",
            color:
              state.whoseTurn === "me"
                ? "var(--accent-fg)"
                : "var(--text-muted)",
          }}
        >
          {state.whoseTurn === "me" ? "Your turn" : "Opponent's turn"}
        </span>
        <span className="pill">Lives {"♥".repeat(state.myLives)}</span>
        <span className="pill">Shots {state.myShots}</span>
        <span className="pill">
          Sunk you / them: {state.mySunk} / {state.opponentSunk}
        </span>
      </div>

      <div
        role="status"
        aria-live="polite"
        className="card"
        style={{ padding: 10, marginBottom: 12 }}
      >
        <span className="small">{state.banner}</span>
      </div>

      <CoordFireInput
        cols={state.myBoard.config.cols}
        rows={state.myBoard.config.rows}
        canFire={state.whoseTurn === "me"}
        tracking={state.tracking}
        onFire={onFire}
      />

      <h3>Enemy waters</h3>
      <p className="small muted" style={{ marginTop: 0 }}>
        Your shots on their chart. Cardinals you reveal point toward
        safe water; the rock is opposite.
      </p>
      <TrackingGrid
        cols={state.myBoard.config.cols}
        rows={state.myBoard.config.rows}
        tracking={state.tracking}
        canFire={state.whoseTurn === "me"}
        onFire={onFire}
      />

      <h3 style={{ marginTop: 24 }}>Your fleet</h3>
      <p className="small muted" style={{ marginTop: 0 }}>
        Your ships, rocks, and cardinals — the opponent's shots
        appear on top.
      </p>
      <MyBoardGrid board={state.myBoard} />

      <button
        className="secondary"
        onClick={onLeave}
        style={{ marginTop: 16 }}
      >
        ← Quit game
      </button>
    </>
  );
}

function CoordFireInput({
  cols,
  rows,
  canFire,
  tracking,
  onFire,
}: {
  cols: number;
  rows: number;
  canFire: boolean;
  tracking: TrackingCell[][];
  onFire: (col: number, row: number) => void;
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = () => {
    if (!canFire) {
      setError("Wait for your turn.");
      return;
    }
    const parsed = parseCoord(value, cols, rows);
    if (!parsed) {
      setError(`"${value}" isn't a valid coordinate.`);
      return;
    }
    if (tracking[parsed.row]?.[parsed.col]?.state !== "hidden") {
      setError(`${coordLabel(parsed.col, parsed.row)} already fired.`);
      return;
    }
    onFire(parsed.col, parsed.row);
    setValue("");
    setError(null);
  };
  return (
    <div
      className="card"
      style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
    >
      <label className="small muted" htmlFor="coord-input-multi">
        Fire at
      </label>
      <input
        id="coord-input-multi"
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="E.g. C7"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        disabled={!canFire}
        style={{
          width: 110,
          textTransform: "uppercase",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      />
      <button onClick={submit} disabled={!canFire || !value.trim()}>
        Fire
      </button>
      {error && (
        <span
          className="small"
          style={{ color: "var(--danger)", flexBasis: "100%" }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

function TrackingGrid({
  cols,
  rows,
  tracking,
  canFire,
  onFire,
}: {
  cols: number;
  rows: number;
  tracking: TrackingCell[][];
  canFire: boolean;
  onFire: (col: number, row: number) => void;
}) {
  const cellSize = "32px";
  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    color: "var(--text-muted)",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  return (
    <div className="card" style={{ padding: 8, overflow: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `auto repeat(${cols}, ${cellSize})`,
          gridTemplateRows: `auto repeat(${rows}, ${cellSize})`,
          gap: 2,
          width: "fit-content",
          margin: "0 auto",
        }}
      >
        <div />
        {Array.from({ length: cols }, (_, c) => (
          <div key={`th-${c}`} style={labelStyle}>
            {String.fromCharCode(65 + c)}
          </div>
        ))}
        {Array.from({ length: rows }, (_, r) => (
          <TrackingRow
            key={`tr-${r}`}
            rowIdx={r}
            cols={cols}
            tracking={tracking}
            canFire={canFire}
            onFire={onFire}
            labelStyle={labelStyle}
          />
        ))}
      </div>
    </div>
  );
}

function TrackingRow({
  rowIdx,
  cols,
  tracking,
  canFire,
  onFire,
  labelStyle,
}: {
  rowIdx: number;
  cols: number;
  tracking: TrackingCell[][];
  canFire: boolean;
  onFire: (col: number, row: number) => void;
  labelStyle: React.CSSProperties;
}) {
  return (
    <>
      <div style={labelStyle}>{rowIdx + 1}</div>
      {Array.from({ length: cols }, (_, c) => {
        const t = tracking[rowIdx]![c]!;
        const isHidden = t.state === "hidden";
        const clickable = canFire && isHidden;
        return (
          <button
            key={`tc-${c}-${rowIdx}`}
            onClick={() => clickable && onFire(c, rowIdx)}
            disabled={!clickable}
            title={coordLabel(c, rowIdx)}
            style={{
              ...cellStyle(t.state),
              minHeight: 0,
              padding: 0,
              fontSize: 11,
              cursor: clickable ? "pointer" : "default",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <TrackingGlyph cell={t} />
          </button>
        );
      })}
    </>
  );
}

function TrackingGlyph({ cell }: { cell: TrackingCell }) {
  switch (cell.state) {
    case "hidden":
      return null;
    case "water":
      return (
        <CellFill>
          <WaveGlyph />
        </CellFill>
      );
    case "cardinal":
      return (
        <CellFill>
          <CardinalBuoy direction={cell.cardinalDirection ?? "N"} />
        </CellFill>
      );
    case "hit":
    case "sunk":
      return <span style={{ fontSize: 14, fontWeight: 700 }}>✕</span>;
    case "grounded":
      return (
        <CellFill>
          <RockGlyph aground />
        </CellFill>
      );
    case "land":
      return null;
  }
}

function MyBoardGrid({ board }: { board: Board }) {
  const { cols, rows } = board.config;
  const cellSize = "28px";
  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    color: "var(--text-muted)",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  return (
    <div className="card" style={{ padding: 8, overflow: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `auto repeat(${cols}, ${cellSize})`,
          gridTemplateRows: `auto repeat(${rows}, ${cellSize})`,
          gap: 2,
          width: "fit-content",
          margin: "0 auto",
        }}
      >
        <div />
        {Array.from({ length: cols }, (_, c) => (
          <div key={`mh-${c}`} style={labelStyle}>
            {String.fromCharCode(65 + c)}
          </div>
        ))}
        {Array.from({ length: rows }, (_, r) => (
          <MyRow
            key={`mr-${r}`}
            rowIdx={r}
            cols={cols}
            board={board}
            labelStyle={labelStyle}
          />
        ))}
      </div>
    </div>
  );
}

function MyRow({
  rowIdx,
  cols,
  board,
  labelStyle,
}: {
  rowIdx: number;
  cols: number;
  board: Board;
  labelStyle: React.CSSProperties;
}) {
  return (
    <>
      <div style={labelStyle}>{rowIdx + 1}</div>
      {Array.from({ length: cols }, (_, c) => {
        const cell = board.cells[rowIdx]![c]!;
        return (
          <div
            key={`mc-${c}-${rowIdx}`}
            style={{
              ...myCellStyle(cell),
              fontSize: 11,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <MyCellGlyph cell={cell} />
          </div>
        );
      })}
    </>
  );
}

function MyCellGlyph({ cell }: { cell: Cell }) {
  // Shows the underlying content AND any incoming-shot overlay.
  // Order matters: shot state takes visual precedence over content.
  if (cell.state === "hit" || cell.state === "sunk") {
    return <span style={{ fontSize: 14, fontWeight: 700, color: "#ffffff" }}>✕</span>;
  }
  if (cell.state === "grounded") {
    return (
      <CellFill>
        <RockGlyph aground />
      </CellFill>
    );
  }
  if (cell.state === "water") {
    // The opponent shot here and it was open water.
    return (
      <CellFill>
        <WaveGlyph />
      </CellFill>
    );
  }
  // Cell hasn't been shot yet — show what's there to us.
  switch (cell.content.kind) {
    case "ship":
      return (
        <span
          style={{
            display: "inline-block",
            width: 12,
            height: 12,
            background: "#1c2530",
            borderRadius: 2,
          }}
        />
      );
    case "hazard":
      // My own rock, not yet shot at by the opponent.
      return (
        <CellFill>
          <RockGlyph />
        </CellFill>
      );
    case "cardinal":
      return (
        <CellFill>
          <CardinalBuoy direction={cell.content.direction} />
        </CellFill>
      );
    case "land":
    case "water":
      return null;
  }
}

function EndCard({
  reason,
  won,
  onAgain,
  onBack,
}: {
  reason: string;
  won?: boolean;
  onAgain: () => void;
  onBack: () => void;
}) {
  const colour =
    won === true
      ? "var(--success)"
      : won === false
        ? "var(--danger)"
        : "var(--border)";
  return (
    <div className="card" style={{ borderColor: colour, borderWidth: 2 }}>
      <h3 style={{ marginTop: 0 }}>
        {won === true
          ? "Victory — fleet sunk."
          : won === false
            ? "Defeat — fleet sunk."
            : "Session ended."}
      </h3>
      <p className="small muted">{reason}</p>
      <button onClick={onAgain}>New game</button>{" "}
      <button className="secondary" onClick={onBack}>
        ← Multi-player hub
      </button>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────

function makeEmptyTracking(cols: number, rows: number): TrackingCell[][] {
  const grid: TrackingCell[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: TrackingCell[] = [];
    for (let c = 0; c < cols; c++) row.push({ state: "hidden" });
    grid.push(row);
  }
  return grid;
}

function cloneBoard(b: Board): Board {
  return {
    ...b,
    cells: b.cells.map((row) => row.map((c) => ({ ...c }))),
    ships: b.ships.map((s) => ({ ...s })),
  };
}

function networkEvent(
  event: RevealResult["event"],
): FireResultMessage["event"] {
  switch (event) {
    case "water":
    case "cardinal":
    case "hit":
    case "sunk":
    case "grounded":
      return event;
    case "land":
    case "already-revealed":
    case "game-over":
      // Defensive — these should never go on the wire (two-player
      // boards have no enclaves). Bucket to "water" so the protocol
      // stays valid.
      return "water";
  }
}

function stateFromEvent(event: FireResultMessage["event"]): CellState {
  switch (event) {
    case "water":
      return "water";
    case "cardinal":
      return "cardinal";
    case "hit":
      return "hit";
    case "sunk":
      return "sunk";
    case "grounded":
      return "grounded";
  }
}

function describeIncoming(r: RevealResult): string {
  switch (r.event) {
    case "water":
      return `Opponent fired and missed. Your turn.`;
    case "cardinal":
      return `Opponent revealed one of your cardinal marks. Your turn.`;
    case "hit":
      return `Opponent hit one of your ships! Your turn.`;
    case "sunk":
      return `Opponent sank your ${r.sunkShipName}. Your turn.`;
    case "grounded":
      return `Opponent grounded on one of your rocks — they lose a life. Your turn.`;
    default:
      return "Your turn.";
  }
}

function describeOutgoing(r: FireResultMessage): string {
  const coord = coordLabel(r.col, r.row);
  switch (r.event) {
    case "water":
      return `${coord} was open water. Opponent's turn.`;
    case "cardinal":
      return `${coord} — cardinal mark (rock is opposite). Opponent's turn.`;
    case "hit":
      return `${coord} hit! Opponent's turn.`;
    case "sunk":
      return `${coord} sank their ${r.sunkShipName ?? "ship"}! Opponent's turn.`;
    case "grounded":
      return `${coord} was a rock — you grounded and lose a life. Opponent's turn.`;
  }
}

function cellStyle(state: CellState): React.CSSProperties {
  switch (state) {
    case "hidden":
      return {
        background: "#1e6091",
        color: "#ffffff",
        border: "1px solid #164a72",
      };
    case "water":
      return {
        background: "#eaf3fb",
        color: "#5d6878",
        border: "1px solid #cdd4dd",
      };
    case "cardinal":
      return {
        background: "#ffffff",
        color: "#1c2530",
        border: "1px solid #cdd4dd",
        padding: 0,
      };
    case "hit":
      return {
        background: "#fca5a5",
        color: "#7f1d1d",
        border: "1px solid #b91c1c",
      };
    case "sunk":
      return {
        background: "#7f1d1d",
        color: "#ffffff",
        border: "1px solid #450a0a",
      };
    case "grounded":
      return {
        background: "#a16207",
        color: "#fef3c7",
        border: "1px solid #713f12",
      };
    case "land":
      return {
        background: "#cdb892",
        color: "#3f3a2c",
        border: "1px solid #b6a075",
      };
  }
}

function myCellStyle(cell: Cell): React.CSSProperties {
  // If the opponent has shot this cell, show their shot's colour;
  // otherwise show a neutral "my chart" background that exposes
  // ship / hazard / cardinal markers underneath.
  if (cell.state !== "hidden") {
    return cellStyle(cell.state);
  }
  // Hidden from opponent = visible to me as my own chart.
  return {
    background: "#f7f8fb",
    border: "1px solid #cdd4dd",
  };
}

// Keep the importer happy — TrackingCell is referenced by glyph
// helper above.
type _Unused = TrackingCell;
void ({} as _Unused);

// Surface TOTAL_SHIPS for tests / future use without unused-var
// lint complaints.
void TOTAL_SHIPS;
